import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TwoFactorSettings } from "@/components/TwoFactorSettings";
import { ShieldCheck, LogOut } from "lucide-react";

export default function SecurityDialog({ open, onOpenChange }) {
  const { logout } = useAuth();
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [cf, setCf] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (nw.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (nw !== cf) { toast.error("New passwords do not match"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      if (data.token) localStorage.setItem("cc_token", data.token);
      toast.success("Password changed. Other devices have been signed out.");
      setCur(""); setNw(""); setCf("");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const logoutAll = async () => {
    try { await api.post("/auth/logout-all"); toast.success("Signed out of all devices."); logout(); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="security-dialog" className="max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[#00C9E0]" />Security</DialogTitle>
          <DialogDescription>Change your password and manage active sessions.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 py-1">
          <div>
            <Label className="text-xs text-slate-600">Current Password</Label>
            <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="mt-1" required data-testid="current-password" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">New Password</Label>
            <Input type="password" value={nw} onChange={(e) => setNw(e.target.value)} className="mt-1" required minLength={8} data-testid="new-password" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Confirm New Password</Label>
            <Input type="password" value={cf} onChange={(e) => setCf(e.target.value)} className="mt-1" required data-testid="confirm-password" />
          </div>
          <Button type="submit" className="w-full" disabled={saving} data-testid="change-password-submit">{saving ? "Saving…" : "Change Password"}</Button>
        </form>
        <Separator />
        <div className="flex items-center justify-between pt-1">
          <div>
            <p className="text-sm font-medium text-slate-800">Active sessions</p>
            <p className="text-xs text-slate-400">Sign out everywhere, including this device.</p>
          </div>
          <Button variant="outline" onClick={logoutAll} data-testid="logout-all-btn"><LogOut className="h-4 w-4" />Log out all</Button>
        </div>
        <Separator />
        <TwoFactorSettings />
      </DialogContent>
    </Dialog>
  );
}
